import { EButtonIntents, ETypographyIntents } from '@src/typings';
import { Dropdown, List, MenuProps } from 'antd';
import { MouseEvent, useEffect, useMemo } from 'react';
import {
    HiEllipsisVertical,
    HiExclamationCircle,
    HiPencilSquare,
    HiPlus,
    HiTrash,
} from 'react-icons/hi2';
import { Button } from '../Button';
import { useTheme, withTheme } from '../ThemeProvider';
import { Typography } from '../Typography';
import { StyledList } from './styles';
import { StickListItem, StickyListProps } from './types';

const CustomListItem = (item: StickListItem) => {
    const { theme } = useTheme();

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        item.onClick?.(event);
    };

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const nodeList = document.querySelectorAll(
                '.ant-dropdown-menu-item'
            );
            if (nodeList && nodeList.length) {
                nodeList.forEach((node) => {
                    (node as HTMLElement).style.padding = '12px';
                });
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
        return () => observer.disconnect();
    }, []);

    const dropdownItems = useMemo<MenuProps['items']>(
        () => [
            {
                key: '1',
                label: (
                    <Typography
                        intent={ETypographyIntents.Regular}
                        style={{
                            color: theme.colors.secondary.main.text,
                        }}
                    >
                        Details
                    </Typography>
                ),
                icon: (
                    <HiExclamationCircle
                        className="mr-2 !min-w-4 !min-h-4 text-base"
                        style={{
                            color: theme.colors.secondary.main.icon,
                        }}
                    />
                ),
                onClick: (event) => {
                    event.domEvent.stopPropagation();
                    item.onDetailClick?.(event.domEvent);
                },
            },
            {
                key: '2',
                label: (
                    <Typography
                        intent={ETypographyIntents.Regular}
                        style={{
                            color: theme.colors.secondary.main.text,
                        }}
                    >
                        Edit
                    </Typography>
                ),
                icon: (
                    <HiPencilSquare
                        className="mr-2 !min-w-4 !min-h-4 text-base"
                        style={{
                            color: theme.colors.secondary.main.icon,
                        }}
                    />
                ),
                onClick: (event) => {
                    event.domEvent.stopPropagation();
                    item.onEditClick?.(event.domEvent);
                },
            },
            {
                key: '3',
                label: (
                    <Typography
                        intent={ETypographyIntents.Regular}
                        style={{
                            color: theme.colors.secondary.main.text,
                        }}
                    >
                        Delete
                    </Typography>
                ),
                icon: (
                    <HiTrash
                        className="mr-2 !min-w-4 !min-h-4 text-base"
                        style={{
                            color: theme.colors.secondary.main.icon,
                        }}
                    />
                ),
                onClick: (event) => {
                    event.domEvent.stopPropagation();
                    item.onDeleteClick?.(event.domEvent);
                },
            },
        ],
        [item, theme]
    );

    return (
        <List.Item key={item.key}>
            <Button
                className={`item ${!item.isActive ? 'regular' : ''}`}
                onClick={handleClick}
                intent={
                    item.isActive ? EButtonIntents.Filled : EButtonIntents.Text
                }
                disabled={item.disabled}
            >
                {item.image ? (
                    <img
                        src={item.image}
                        width={16}
                        className="rounded-full border"
                        alt="menu item"
                    />
                ) : item.icon ? (
                    item.icon
                ) : (
                    <HiExclamationCircle
                        className="mr-1 min-w-4 min-h-4 text-base"
                        style={{
                            color: item.isActive
                                ? 'inherit'
                                : theme.colors.secondary.main.icon,
                        }}
                    />
                )}
                <Typography intent={ETypographyIntents.Action}>
                    {item.label}
                </Typography>
                {item.hasAction && (
                    <Dropdown
                        trigger={['click']}
                        placement="bottomCenter"
                        arrow={false}
                        menu={{
                            items: dropdownItems,
                            selectable: false,
                        }}
                    >
                        <HiEllipsisVertical
                            className="mr-1 min-w-4 min-h-4 text-base"
                            style={{
                                color: item.isActive
                                    ? 'inherit'
                                    : theme.colors.secondary.main.icon,
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        />
                    </Dropdown>
                )}
            </Button>
        </List.Item>
    );
};

function StickyListComponent({
    title,
    items,
    onNewItemClick,
    className = '',
    titleClassName = '',
    newItemClassName = '',
    newItemTitle = 'New Item',
}: StickyListProps) {
    const { theme } = useTheme();

    return (
        <StyledList
            header={
                title ? (
                    <Typography
                        intent={ETypographyIntents.H3}
                        className={`p-6 pb-4 ${titleClassName}`}
                        style={{ color: theme.colors.secondary.main.text }}
                    >
                        {title}
                    </Typography>
                ) : null
            }
            dataSource={items}
            className={`min-w-64 ${className}`}
            renderItem={(item) => (
                <CustomListItem {...(item as StickListItem)} />
            )}
            footer={
                onNewItemClick && (
                    <Button
                        className={`item ${newItemClassName}`}
                        onClick={onNewItemClick}
                        intent={EButtonIntents.Text}
                    >
                        <HiPlus
                            className="mr-1 min-w-4 min-h-4 text-base"
                            style={{ color: theme.colors.primary.main.filled }}
                        />
                        <Typography intent={ETypographyIntents.Action}>
                            {newItemTitle}
                        </Typography>
                    </Button>
                )
            }
        />
    );
}

StickyListComponent.displayName = 'StickyListComponent';

export const StickyList = withTheme(StickyListComponent);
